import { useState } from 'react'
import { Avatar, Box, Flex } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import { ADVISOR_PROFILE_ACTIONS } from '@/modules/advisorsPage/components/AdvisorWorkspaceProfile/AdvisorWorkspaceProfile.config'
import type { AdvisorWorkspaceProfileProps } from '@/modules/advisorsPage/components/AdvisorWorkspaceProfile/AdvisorWorkspaceProfile.types'
import { CreateEventModal } from '@/modules/advisorsPage/components/CreateEventModal/CreateEventModal'

import { useUserStore } from '@/store/user.store'

import { ButtonMenu } from '@/components/ButtonMenu/ButtonMenu'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { FORM_MESSAGES_MAP } from '@/constants/formMessages.constants'

import classes from './AdvisorWorkspaceProfile.module.css'

export function AdvisorWorkspaceProfile({
  advisor,
  containerRef,
  isAdvisorAllowed,
  isMutating,
  onChatOpen,
  onGrantAccess,
  onRevokeAccess,
}: AdvisorWorkspaceProfileProps) {
  const user = useUserStore.use.user()

  const [menuLabel, setMenuLabel] = useState<string>('Work with a planner')
  const [opened, { close, open }] = useDisclosure()

  const displayName = `${advisor.firstName} ${advisor.lastName}`

  const handleMeetingCreate = () => {
    if (user?.id) {
      const appointmentCount = user.appointmentsCount || 0

      if (appointmentCount >= import.meta.env.VITE_MAX_APPOINTMENTS) {
        notifications.show({
          color: 'red',
          message: FORM_MESSAGES_MAP.MAX_APPOINTMENTS,
          title: 'Error creating event',
        })
      } else {
        open()
      }
    }
  }

  const handleItemSelect = (action?: string) => {
    if (!action) return

    switch (action) {
      case 'grantAccess':
        onGrantAccess()
        break
      case 'revokeAccess':
        onRevokeAccess()
        break
      case 'chat':
        onChatOpen()
        break
      case 'scheduleMeeting':
        handleMeetingCreate()
        break
      default:
        break
    }
  }

  const actions = isAdvisorAllowed
    ? ADVISOR_PROFILE_ACTIONS.filter(item => item.param !== 'grantAccess')
    : ADVISOR_PROFILE_ACTIONS.filter(item => item.param !== 'revokeAccess')

  return (
    <>
      <ScrollArea
        classNames={{
          viewport: classes.viewport,
        }}
        flex={1}
        scrollbars="y"
      >
        <Flex className={classes.container}>
          <Box>
            <Avatar
              alt={displayName}
              className={classes.avatar}
              src={advisor.avatar}
            />

            <Title variant="h1_big">{displayName}</Title>

            {advisor.bio && (
              <Text mt={16} variant="body_1">
                {advisor.bio}
              </Text>
            )}

            <ButtonMenu
              items={actions}
              label={menuLabel}
              loading={isMutating}
              mt={32}
              onClose={() => setMenuLabel('Work with a planner')}
              onItemSelect={handleItemSelect}
              onOpen={() => setMenuLabel('Close')}
            />
          </Box>
        </Flex>
      </ScrollArea>

      <CreateEventModal
        advisor={advisor}
        containerRef={containerRef}
        onClose={close}
        opened={opened}
      />
    </>
  )
}
